import { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { handleExternal, handleSecondaryViewOpen, handleSidebarOpen } from 'store/appSlice';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import BitmovinPlayer from 'components/BitmovinPlayer';
import { useLocation, useParams } from 'react-router-dom';
import { RootState } from 'store';
import { Menu } from 'components/Menu';
import { ChevronDownIcon } from 'components/icons/ChevronDownIcon';
import { SecondaryMenuItem } from 'components/Menu/SecondaryMenuItem';
import api from 'services/api';
import { setSeekVideoShot } from 'store/videoSeektimeSlice';
// import { ImageIcon } from 'components/icons/ImageIcon';
// import { SummaryIcon } from 'components/icons/SummaryIcon';
import { Share2Icon } from 'components/icons/ShareIcon2';
import { ThumbUpIcon } from 'components/icons/ThumbUpIcon';
import { FolderIcon } from 'components/icons/FolderIcon';
import Survey from 'components/VideoTabContent/Survey';
import Speakers from 'components/VideoTabContent/Speakers';
import Resources from 'components/Resources';
import Feedback from 'components/Feedback';
import { CloseIcon } from 'components/icons/CloseIcon';
import Share from 'components/Share';
import { useWindowSize } from 'hooks';
// import { handleSummaries } from 'store/summarySlice';
import { handlePeople } from 'store/peopleSlice';
import { handleResource } from 'store/resourceSlice';
import { Box, styled } from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Drawer from '@mui/material/Drawer';
import PerfectScrollbar from 'react-perfect-scrollbar';
import ExternalTimelineView from 'components/VideoTabContent/ExternalTimelineView';
import { ExternalTabConfig } from './Tabs';

import ViewTimelineOutlinedIcon from '@mui/icons-material/ViewTimelineOutlined';
import honeywell from '../../assets/img/logo/honeywell.png';

import './index.css';

const AntTabs = styled(Tabs)({
  minHeight: 36,
  alignItems: 'center',
  position: 'relative',
  '.right-scroll-button, .left-scroll-button': {
    display: 'flex !important'
  },
  '.MuiTabs-scrollButtons': {
    height: '32px !important',
    width: 32
  },
  '& .MuiTabs-indicator': {
    display: 'none'
  },
  '& .MuiTabs-scrollButtons': {
    borderRadius: '100%',
    height: '100%',
    zIndex: 3,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.1)'
    }
  },
  '& .MuiTabs-scroller': {
    zIndex: 2
  }
});

const AntTab = styled((props: any) => <Tab disableRipple {...props} />)(({ theme }) => ({
  textTransform: 'none',
  padding: '4px 12px !important',
  minHeight: 32,
  minWidth: 0,
  [theme.breakpoints.up('sm')]: {
    minWidth: 0
  },
  fontWeight: 400,
  fontSize: 12.5,
  marginRight: 20,
  color: '#111',
  backgroundColor: 'white',
  borderRadius: 8,
  fontFamily: 'Lato, sans-serif',
  '&:hover': {
    color: '#111',
    opacity: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.1)'
  },
  '&.Mui-selected': {
    color: 'black'
  },
  '&.Mui-focusVisible': {
    backgroundColor: '#d1eaff'
  }
}));

const Puller = styled(Box)(({ theme }) => ({
  width: 30,
  height: 6,
  backgroundColor: 'rgb(33, 33, 33)',
  borderRadius: 3,
  position: 'absolute',
  top: 8,
  left: 'calc(50% - 15px)'
}));

const ExternalVideoPage = (): JSX.Element => {
  const dispatch = useDispatch();
  const { videoId } = useParams();
  const [currentVideo, setCurrentVideo] = useState(null as any);
  const seekTimestamp = useSelector((state: RootState) => state.videoSeektime);
  const [currentVideoShot, setCurrentVideoShot] = useState(seekTimestamp.videoSeek.videoShotIdx);
  const [selectedTab, setSelectedTab] = useState(6); // default is timeline tab
  const [showMore, setShowMore] = useState(false);
  const [cinemaViewport, setCinemaViewport] = useState(false);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);
  const [pollsData, setPollsData] = useState([]);
  const [currentPollId, setCurrentPollId] = useState(null as any);
  const [currentPoll, setCurrentPoll] = useState(null as any);
  const [customQuestion, setCustomQuestion] = useState({} as any);
  const [baseUrl, setBaseUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [shareLink, setShareLink] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [topSectionHeight, setTopSectionHeight] = useState(null as any);
  const topRef = useRef(document.createElement('div'));
  const [open, setOpen] = useState(false);
  const [bottomBarHeight, setBottomBarHeight] = useState<number>(0);
  const [tabs, setTabs] = useState<string[]>([]);
  const [origin, setOrigin] = useState<string | null>(''); // if video is from replay page e.g: honeywell/live

  useEffect(() => {
    const handleResize = () => {
      // Get the height of the window
      const windowHeight = window.innerHeight;

      // Get the height of the document body
      const bodyHeight = document.body.clientHeight;

      // Calculate the bottom bar height
      const calculatedBottomBarHeight = windowHeight - bodyHeight;

      // Set the bottom bar height in state
      setBottomBarHeight(calculatedBottomBarHeight);
    };

    // Attach the resize event listener
    window.addEventListener('resize', handleResize);

    // Initial calculation on mount
    handleResize();

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  function removeURLParameter(url: string, parameterName: string) {
    const urlParts = url.split('?');
    if (urlParts.length >= 2) {
      const prefix = encodeURIComponent(parameterName) + '=';
      const queryStrings = urlParts[1].split('&').filter((item) => {
        return !item.startsWith(prefix);
      });

      if (queryStrings.length > 0) {
        return urlParts[0] + '?' + queryStrings.join('&');
      } else {
        return urlParts[0];
      }
    }
    return url;
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const externalToken = searchParams.get('token');
    dispatch(handleExternal(true));
    if (externalToken) {
      localStorage.setItem('externalToken', externalToken);
      const currentURL = window.location.href;
      const updatedURL = removeURLParameter(currentURL, 'token');
      window.history.replaceState(null, '', updatedURL);
    }

    return () => {
      dispatch(handleExternal(false));
    };
  }, []);

  useEffect(() => {
    if (pollsData.length === 0) {
      setTimeout(() => {
        setIsOpen(true);
      }, 120000);
    } else {
      setCurrentPoll(pollsData[0]);
      pollsData.forEach((poll: any) => {
        setTimeout(() => {
          setCurrentPollId(poll.hash_id);
        }, poll.display_time * 1000);
      });
    }
  }, [pollsData]);

  const searchParams = new URLSearchParams(location.search);
  const getTimestamp = searchParams.get('timestamp');
  const timestamp = useMemo(() => {
    return getTimestamp;
  }, [getTimestamp]);

  useEffect(() => {
    const code = searchParams.get('code');
    const baseUrl = searchParams.get('base_url');
    const origin = searchParams.get('origin');
    setOrigin(origin);

    if (baseUrl) {
      localStorage.setItem('externalBaseUrl', baseUrl);
      setBaseUrl(baseUrl);
    }
    api.get(`api/videos/${videoId}/external-details/?code=${code}`).then((res) => {
      setCurrentVideo(res.data);
      setLoading(false);
    });
    // api.get(`/api/videos/${videoId}/external-summaries/`).then((res) => dispatch(handleSummaries(res.data)));
    api.get(`api/videos/${videoId}/external-speakers/`).then((res) => dispatch(handlePeople(res.data)));
    api.get(`api/videos/${videoId}/external-resources/`).then((res) => dispatch(handleResource(res.data)));
    api.get(`api/videos/${videoId}/external-polls/`).then((res) => setPollsData(res.data));
    api.get(`/api/workspace/tts-question/`).then((res) => {
      setCustomQuestion(res.data);
    });
    api.get(`api/videos/${videoId}/external-share-link/`).then((res) => {
      setShareLink(res.data.external_link);
    });
  }, [location.search, history, videoId]);

  useEffect(() => {
    if (currentVideo) {
      const videoType = currentVideo.details.video_source.toLowerCase() as string;
      api.post(`api/videos/${videoId}/external-view/`, {
        viewer: currentVideo.viewer,
        recordingViewDurationInMinutes: 1
      });
      if (!origin) {
        switch (videoType) {
          case 'zoom_meeting':
            setTabs(['Timeline']);
            break;
          case 'zoom_webinar':
            setTabs(['Timeline', 'Resources']);
            break;
          case 'zoom_event':
            setTabs(['Timeline', 'Resources', 'Share', 'Feedback']);
            break;
          case 'unknown':
            setTabs([]);
            break;
          default:
            setTabs(['Timeline']);
            break;
        }
      } else {
        setTabs(['Timeline']);
      }
    }
  }, [currentVideo]);

  useEffect(() => {
    setCurrentVideoShot(seekTimestamp.videoSeek.videoShotIdx);
  }, [seekTimestamp.videoSeek]);

  useEffect(() => {
    dispatch(handleSidebarOpen(false));
    dispatch(handleSecondaryViewOpen({ open: false }));
  }, []);

  const jumpToVideo = (videoId: string) => {
    const videoElement = document.getElementById(videoId);
    if (videoElement) {
      videoElement.scrollIntoView();
      const closeMenu = document.getElementById('menu-close');
      closeMenu?.click();
    }
  };
  const [width] = useWindowSize();

  useEffect(() => {
    if (width) {
      const wideScreen = width > 768;
      setIsMobile(!wideScreen);
    }
  }, [width]);

  useLayoutEffect(() => {
    if (isMobile) {
      setTopSectionHeight(topRef.current.clientHeight);
      setSelectedTab(0);
      handleDrawerClose();
    } else {
      setTopSectionHeight(0);
    }
  }, [isMobile, width, topRef.current.clientHeight]);

  // useEffect(() => {
  //   const activeTab = origin ? 2 : 1;
  //   setSelectedTab(activeTab);
  // }, [origin]);

  const menu = {
    Feedback: 0,
    Resources: 1,
    Summary: 2,
    Thumbnails: 3,
    Share: 4,
    Survey: 5,
    Timeline: 6
  } as const;

  const handleChange = (selected: string) => {
    const selectedText = selected as keyof typeof menu;
    const tabIndex = menu[selectedText];
    if (tabIndex >= 0) {
      setSelectedTab(tabIndex);
    }
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
    setSelectedTab(-1);
  };

  const videoContent = () => (
    <div>
      <div
        className={`player-wrapper external-player ${
          cinemaViewport || isMobile ? '' : 'ml-4 rounded-md mr-4 md:mr-3'
        } overflow-hidden`}
        ref={topRef}
      >
        {currentVideo?.details?.video_shots?.[currentVideoShot]?.hls_location &&
        currentVideo?.details?.video_shots?.[currentVideoShot]?.status !== 'pending' ? (
          <BitmovinPlayer
            videoShot={currentVideo?.details?.video_shots?.[currentVideoShot]}
            cinemaViewport={cinemaViewport}
            setCinemaViewport={setCinemaViewport}
            currentVideo={currentVideo}
            setHovered={isMobile ? setHovered : undefined}
            timestamp={timestamp}
          />
        ) : (
          <div className="bg-black text-white flex items-center justify-center pt-[55%] relative">
            <p className="absolute top-2/4">{loading ? '' : 'This video is not available yet'}</p>
          </div>
        )}
      </div>
      <div className="ml-4 mr-3 video-name">
        {currentVideo?.details?.name && (
          <p className="break-words text-xl font-semibold whitespace-normal mt-3 line-clamp-2">
            {currentVideo.details.name}
          </p>
        )}
        {currentVideo?.details?.description && (
          <div className="p-3 rounded-md relative mt-3 description">
            <p className={`${showMore ? '' : 'line-clamp-2'} whitespace-pre-wrap text-sm`}>
              {currentVideo.details.description}
            </p>

            {showMore ? (
              <div className="block">
                <p
                  className="bottom-3 font-semibold right-3 bg-white cursor-pointer"
                  onClick={() => setShowMore(!showMore)}
                >
                  show less
                </p>
              </div>
            ) : (
              <p
                className="absolute bottom-3 font-semibold right-3 bg-white cursor-pointer"
                onClick={() => setShowMore(!showMore)}
              >
                ...more
              </p>
            )}
          </div>
        )}
      </div>
      {baseUrl !== 'https://demo.videospace.ai' && customQuestion.is_enabled ? (
        <div
          className={`w-full md:hidden flex items-center justify-between px-6 flex-wrap mt-6 mb-4 transition duration-500 ease-in-out z-50`}
        >
          <div className="flex items-center text-black font-black text-lg truncate w-48">
            <img src={honeywell} alt="honeywell-logo" className="h-[50px] max-w-full object-contain" />
          </div>
          <button
            type="button"
            className="peer-focus:block bg-red-main border-gray-100 text-white px-3 py-2 rounded-md ml-4"
            onClick={() => {
              setOpen(true);
              setSelectedTab(5);
            }}
          >
            Talk to Sales
          </button>
        </div>
      ) : (
        ''
      )}
      <div className="md:hidden flex justify-center w-full items-center py-1 px-2">
        <AntTabs
          value={selectedTab}
          // onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="scrollable force tabs example"
        >
          {ExternalTabConfig.map(
            ({ key, label, icon }, index) =>
              tabs.includes(key) && (
                <AntTab
                  key={key}
                  icon={icon(selectedTab === index)}
                  label={label}
                  onClick={() => {
                    handleChange(label);
                  }}
                />
              )
          )}
        </AntTabs>
      </div>
    </div>
  );

  return (
    <>
      <div className="h-full">
        {!cinemaViewport && !isMobile && (
          <div className="w-full hidden md:flex items-center justify-between px-4 flex-wrap mb-4">
            <div className="flex items-center text-black font-black text-lg truncate w-48">
              <img src={honeywell} alt="honeywell-logo" className="h-[50px] max-w-full object-contain" />
            </div>
            <div className="min-h-[50px] flex items-center py-[10px] px-1 flex-wrap mr-auto">
              {baseUrl !== 'https://honeywell-hq.videospace.ai' ? (
                <div className="bg-white rounded-lg max-w-[calc(100%-100px)] ml-4 min-w-fit">
                  <Menu
                    menus={(currentVideo?.details?.video_shots ?? []).map((menuVideoShot: any, idx: number) => ({
                      id: menuVideoShot.shot_hash_id,
                      name: menuVideoShot.name,
                      selected:
                        currentVideo?.details?.video_shots[currentVideoShot]?.shot_hash_id ===
                        menuVideoShot?.shot_hash_id,
                      onClick: () => {
                        dispatch(setSeekVideoShot({ time: 0, videoShotIdx: idx }));
                        setCurrentVideoShot(idx);
                      }
                    }))}
                    align="center"
                    position="absolute"
                    top="mt-8"
                    width={265}
                    MenuItem={SecondaryMenuItem}
                    additionalClasses="flex justify-center"
                  >
                    <div className="pl-4 pr-2 h-8 leading-8 rounded-lg text-[13px] font-lato font-bold cursor-pointer w-full flex relative items-center mx-auto z-10 bg-black bg-opacity-5 hover:bg-opacity-10">
                      <span className="truncate w-[calc(100%-24px)]">
                        {currentVideo?.details?.video_shots?.[currentVideoShot]?.name}
                      </span>
                      <ChevronDownIcon />
                    </div>
                  </Menu>
                </div>
              ) : (
                ''
              )}
            </div>
            <div className="hidden md:flex ml-auto items-center py-1">
              {baseUrl !== 'https://honeywell-hq.videospace.ai' && tabs.find((tab) => tab === 'Feedback') ? (
                <>
                  <div
                    className={`relative group cursor-pointer bg-white mr-0 icon-group pt-3 pb-2 px-2 text-center min-w-[90px] font-semibold ${
                      selectedTab === 0 ? 'text-black' : 'text-gray-icon hover:text-black'
                    }`}
                  >
                    <div
                      onClick={() => {
                        setSelectedTab(0);
                      }}
                    >
                      <div className={clsx('flex h-full items-center justify-center rounded-sm mx-auto')}>
                        <ThumbUpIcon
                          className={`w-6 h-6 ${
                            selectedTab === 0 ? 'stroke-black' : 'stroke-gray-icon group-hover:stroke-black'
                          }`}
                        />
                      </div>
                      <div className="relative w-fit mx-auto">
                        <span
                          className={`text-xs leading-none ${selectedTab === 0 ? 'video-tab' : 'video-tab-inactive'}`}
                        >
                          Feedback
                        </span>
                      </div>
                    </div>
                    {pollsData.length == 0 && !isFeedbackSubmitted && isOpen && (
                      <div
                        className="absolute z-50 w-56 p-4 mt-4 text-sm text-white bg-black rounded shadow-lg cursor-default opacity-75"
                        onClick={(event: any) => {
                          event?.stopPropagation();
                          setSelectedTab(0);
                        }}
                      >
                        <div className="flex justify-between">
                          <span>Please take a moment to tell us how we’re doing.</span>
                          <CloseIcon
                            className="w-5 h-5 cursor-pointer opacity-70 hover:opacity-100 absolute right-1 top-1"
                            onClick={() => setIsOpen(false)}
                          />
                        </div>
                        <div className="absolute w-3 h-3 transform rotate-45 bg-gray-500 pointer"></div>
                      </div>
                    )}
                    {pollsData.map((poll: any) => (
                      <div
                        className={`absolute z-50 w-56 p-4 mt-4 text-sm text-white bg-black rounded shadow-lg cursor-default opacity-75 ${
                          currentPollId === poll.hash_id ? 'block' : 'hidden'
                        }`}
                        key={poll.hash_id}
                        onClick={() => {
                          setSelectedTab(0);
                          setCurrentPoll(poll);
                        }}
                      >
                        <div className="flex justify-between">
                          <span>Please take a moment to complete the {poll.title} poll.</span>
                          <CloseIcon
                            className="w-5 h-5 cursor-pointer opacity-70 hover:opacity-100 absolute right-1 top-1"
                            onClick={(event: any) => {
                              event?.stopPropagation();
                              setCurrentPollId(null);
                            }}
                          />
                        </div>
                        <div className="absolute w-3 h-3 transform rotate-45 bg-gray-500 pointer"></div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                ''
              )}
              {tabs.find((tab) => tab === 'Resources') ? (
                <div
                  className={`group cursor-pointer bg-white mr-0 icon-group pt-3 pb-2 px-2 text-center min-w-[90px] font-semibold ${
                    selectedTab === 1 ? 'text-black' : 'text-gray-icon hover:text-black'
                  }`}
                  onClick={() => {
                    setSelectedTab(1);
                  }}
                >
                  <div className={clsx('flex h-full items-center justify-center rounded-sm mx-auto')}>
                    <FolderIcon
                      className={`w-6 h-6 ${
                        selectedTab === 1 ? 'fill-black' : 'fill-gray-icon group-hover:fill-black'
                      }`}
                    />
                  </div>
                  <div className="relative w-fit mx-auto">
                    <span className={`text-xs leading-none ${selectedTab === 1 ? 'video-tab' : 'video-tab-inactive'}`}>
                      Resources
                    </span>
                  </div>
                </div>
              ) : (
                <></>
              )}
              {/* {tabs.find((tab) => tab === 'Summary') ? (
                <div
                  className={`group cursor-pointer bg-white mr-0 icon-group pt-3 pb-2 px-2 text-center min-w-[90px] font-semibold ${
                    selectedTab === 2 ? 'text-black' : 'text-gray-icon hover:text-black'
                  }`}
                  onClick={() => {
                    setSelectedTab(2);
                  }}
                >
                  <div className={clsx('flex h-full items-center justify-center rounded-sm mx-auto')}>
                    <SummaryIcon
                      className={`w-6 h-6 ${
                        selectedTab === 2 ? 'fill-black' : 'fill-gray-icon group-hover:fill-black'
                      }`}
                    />
                  </div>
                  <div className="relative w-fit mx-auto">
                    <span className={`text-xs leading-none ${selectedTab === 2 ? 'video-tab' : 'video-tab-inactive'}`}>
                      Summary
                    </span>
                  </div>
                </div>
              ) : (
                <></>
              )} */}
              {tabs.find((tab) => tab === 'Timeline') && (
                <div
                  className={`group cursor-pointer bg-white mr-0 icon-group pt-3 pb-2 px-2 text-center min-w-[90px] font-semibold ${
                    selectedTab === 6 ? 'text-black' : 'text-gray-icon hover:text-black'
                  }`}
                  onClick={() => {
                    setSelectedTab(6);
                  }}
                >
                  <div className={clsx('flex h-full items-center justify-center rounded-sm mx-auto')}>
                    <ViewTimelineOutlinedIcon
                      className={`w-6 h-6 ${
                        selectedTab === 6 ? 'fill-black' : 'fill-gray-icon group-hover:fill-black'
                      }`}
                    />
                  </div>
                  <div className="relative w-fit mx-auto">
                    <span className={`text-xs leading-none ${selectedTab === 6 ? 'video-tab' : 'video-tab-inactive'}`}>
                      Timeline
                    </span>
                  </div>
                </div>
              )}
              {/* {tabs.find((tab) => tab === 'Thumbnails') ? (
                <div
                  className={`group cursor-pointer bg-white mr-0 icon-group pt-3 pb-2 px-2 text-center min-w-[90px] font-semibold ${
                    selectedTab === 3 ? 'text-black' : 'text-gray-icon hover:text-black'
                  }`}
                  onClick={() => {
                    setSelectedTab(3);
                  }}
                >
                  <div className={clsx('flex h-full items-center justify-center rounded-sm mx-auto')}>
                    <ImageIcon
                      className={`w-6 h-6 ${
                        selectedTab === 3 ? 'fill-black' : 'fill-gray-icon group-hover:fill-black'
                      }`}
                    />
                  </div>
                  <div className="relative w-fit mx-auto">
                    <span className={`text-xs leading-none ${selectedTab === 3 ? 'video-tab' : 'video-tab-inactive'}`}>
                      Thumbnails
                    </span>
                  </div>
                </div>
              ) : (
                <></>
              )} */}
              {baseUrl !== 'https://honeywell-hq.videospace.ai' && tabs.find((tab) => tab === 'Share') ? (
                <div
                  className={`group cursor-pointer bg-white mr-0 icon-group pt-3 pb-2 px-2 text-center min-w-[90px] font-semibold ${
                    selectedTab === 4 ? 'text-black' : 'text-gray-icon hover:text-black'
                  }`}
                  onClick={() => {
                    setSelectedTab(4);
                  }}
                >
                  <div className={clsx('flex h-full items-center justify-center rounded-sm mx-auto')}>
                    <Share2Icon
                      className={`w-6 h-6 ${
                        selectedTab === 4 ? 'stroke-black' : 'stroke-gray-icon group-hover:stroke-black'
                      }`}
                    />
                  </div>
                  <div className="relative w-fit mx-auto">
                    <span className={`text-xs leading-none ${selectedTab === 4 ? 'video-tab' : 'video-tab-inactive'}`}>
                      Share
                    </span>
                  </div>
                </div>
              ) : (
                ''
              )}
              {baseUrl !== 'https://demo.videospace.ai' && customQuestion.is_enabled ? (
                <button
                  type="button"
                  className="peer-focus:block bg-red-main border-gray-100 text-white px-3 py-2 rounded-md ml-4"
                  onClick={() => {
                    setSelectedTab(5);
                  }}
                >
                  Talk to Sales
                </button>
              ) : (
                ''
              )}
            </div>
          </div>
        )}
        <div className={`w-full flex`}>
          <div className={`w-full h-full text-black`}>
            {isMobile ? (
              <>{videoContent()}</>
            ) : (
              <PerfectScrollbar style={{ maxHeight: cinemaViewport ? '100vh' : `calc(100vh - 110px)` }}>
                {videoContent()}
              </PerfectScrollbar>
            )}

            <div className="min-h-[50px] flex items-center py-[10px] px-1 flex-wrap mr-auto md:hidden">
              {baseUrl !== 'https://honeywell-hq.videospace.ai' ? (
                <div className="bg-white rounded-lg w-full mx-4 min-w-fit">
                  <Menu
                    menus={(currentVideo?.details?.video_shots ?? []).map((menuVideoShot: any, idx: number) => ({
                      id: menuVideoShot.shot_hash_id,
                      name: menuVideoShot.name,
                      selected:
                        currentVideo?.details?.video_shots[currentVideoShot]?.shot_hash_id ===
                        menuVideoShot?.shot_hash_id,
                      onClick: () => {
                        dispatch(setSeekVideoShot({ time: 0, videoShotIdx: idx }));
                        setCurrentVideoShot(idx);
                      }
                    }))}
                    align="center"
                    position="absolute"
                    top="mt-8"
                    width={'100%'}
                    MenuItem={SecondaryMenuItem}
                    additionalClasses="flex justify-center"
                  >
                    <div className="pl-4 pr-2 h-8 leading-8 rounded-lg text-[13px] font-lato font-bold cursor-pointer w-full flex relative items-center mx-auto z-10 bg-black bg-opacity-5 hover:bg-opacity-10">
                      <span className="truncate w-[calc(100%-24px)]">
                        {currentVideo?.details?.video_shots?.[currentVideoShot]?.name}
                      </span>
                      <ChevronDownIcon />
                    </div>
                  </Menu>
                </div>
              ) : (
                ''
              )}
            </div>
            {isMobile && (
              <Drawer
                anchor="bottom"
                open={open}
                onClose={handleDrawerClose}
                ModalProps={{
                  keepMounted: true
                }}
                sx={{
                  '& .MuiDrawer-paper': {
                    maxHeight: `calc(100% - ${topSectionHeight}px)`,
                    height: '100%'
                  }
                }}
              >
                <Box
                  sx={{
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    visibility: 'visible',
                    paddingBottom: 3
                  }}
                >
                  <Puller />
                  <CloseIcon
                    className="ml-auto w-8 h-8 cursor-pointer opacity-70 hover:opacity-100 absolute top-7 right-5 z-50"
                    onClick={handleDrawerClose}
                  />
                </Box>
                <Box
                  sx={{
                    pb: 2,
                    height: 'calc(100% - 24px)',
                    overflow: 'hidden'
                  }}
                >
                  <div className="mt-2">
                    <div>
                      {selectedTab === 1 && (
                        <>
                          <p
                            className={`mb-2 border-b ${
                              topSectionHeight ? 'text-xl font-semibold pt-0 pb-4 px-4' : 'font-bold text-base p-2'
                            }`}
                          >
                            Resources
                          </p>
                          <PerfectScrollbar
                            style={{
                              maxHeight: topSectionHeight ? `calc(100vh - ${topSectionHeight + 40}px)` : '50vh'
                            }}
                            options={{ suppressScrollY: false, suppressScrollX: true }}
                          >
                            {<Speakers topSectionHeight={topSectionHeight - bottomBarHeight} external={true} />}
                            {selectedTab === 1 && baseUrl !== 'https://honeywell-hq.videospace.ai' && (
                              <div className={`${selectedTab === 1 ? 'mt-2' : ''}`}>
                                <Resources topSectionHeight={topSectionHeight - bottomBarHeight} external={true} />
                              </div>
                            )}
                          </PerfectScrollbar>
                        </>
                      )}
                      <div
                        className={`h-full rounded-md text-black text-sm w-full overflow-hidden md:w-96 ${
                          selectedTab === 0 ? 'block' : 'hidden'
                        }`}
                      >
                        <p
                          className={`mb-2 border-b ${
                            topSectionHeight ? 'text-xl font-semibold pt-0 pb-4 px-4' : 'font-bold text-base p-2'
                          }`}
                        >
                          Feedback
                        </p>
                        <Feedback
                          videoId={videoId}
                          viewer={currentVideo?.viewer}
                          setIsFeedbackSubmitted={setIsFeedbackSubmitted}
                          pollsData={pollsData}
                          setCurrentPoll={setCurrentPoll}
                          currentPoll={currentPoll}
                          topSectionHeight={topSectionHeight - bottomBarHeight}
                        />
                      </div>
                      {selectedTab === 4 && (
                        <div>
                          <Share shareLink={shareLink} topSectionHeight={topSectionHeight - bottomBarHeight} />
                        </div>
                      )}
                      {selectedTab === 5 && (
                        <div>
                          <p
                            className={`mb-2 border-b ${
                              topSectionHeight ? 'text-xl font-semibold pt-0 pb-4 px-4' : 'font-bold text-base p-2'
                            }`}
                          >
                            Talk to sale
                          </p>
                          <Survey
                            videoId={videoId}
                            viewer={currentVideo?.viewer}
                            customQuestion={customQuestion}
                            topSectionHeight={topSectionHeight - bottomBarHeight}
                          />
                        </div>
                      )}
                      {selectedTab === 2 && <div></div>}
                      <div className={`${selectedTab === 3 ? 'block' : 'hidden'}`}></div>
                      <div className={`${selectedTab === 6 ? 'block' : 'hidden'}`}>
                        {videoId && currentVideo && (
                          <ExternalTimelineView
                            videoHashId={videoId}
                            videoShotId={currentVideo.details.video_shots?.[currentVideoShot]?.shot_hash_id}
                            aesKeyId={currentVideo.details?.keyframe_aes_key_id}
                            encrytedKeys={currentVideo?.details?.enc_data}
                            topSectionHeight={topSectionHeight - bottomBarHeight}
                            currentVideoShot={currentVideoShot}
                          ></ExternalTimelineView>
                        )}
                      </div>
                    </div>
                  </div>
                </Box>
              </Drawer>
            )}
          </div>
          {!isMobile && (
            <div className="hidden md:block h-full sidebar">
              <div className={`${cinemaViewport ? 'hidden' : 'block h-full'}`}>
                {selectedTab === 1 && <Speakers />}
                {selectedTab === 1 && baseUrl !== 'https://honeywell-hq.videospace.ai' && (
                  <div className={`${selectedTab === 1 ? 'mt-2' : ''}`}>
                    <Resources />
                  </div>
                )}
                <div
                  className={`border h-full mr-3 rounded-md text-black text-sm w-full overflow-hidden md:w-96 ${
                    selectedTab === 0 ? 'block' : 'hidden'
                  }`}
                >
                  <Feedback
                    videoId={videoId}
                    viewer={currentVideo?.viewer}
                    pollsData={pollsData}
                    setIsFeedbackSubmitted={setIsFeedbackSubmitted}
                    setCurrentPoll={setCurrentPoll}
                    currentPoll={currentPoll}
                  />
                </div>
                {selectedTab === 4 && (
                  <div>
                    <Share shareLink={shareLink} />
                  </div>
                )}
                {selectedTab === 5 && (
                  <div>
                    <Survey videoId={videoId} viewer={currentVideo?.viewer} customQuestion={customQuestion} />
                  </div>
                )}
                {/* <div className={`${selectedTab === 2 ? 'block' : 'hidden'}`}>
                  <SummaryView
                    external={true}
                    id={'external' + '-' + (videoId ? videoId : '') + '-' + 'Summary'}
                    videoShotId={currentVideo?.details.video_shots?.[currentVideoShot]?.shot_hash_id}
                    currentVideoShot={currentVideoShot}
                  />
                </div>
                <div className={`${selectedTab === 3 ? 'block' : 'hidden'}`}>
                  <ExternalThumbnailView
                    id={'external' + '-' + (videoId ? videoId : '') + '-' + 'Thumbnail'}
                    videoShotId={currentVideo?.details.video_shots?.[currentVideoShot]?.shot_hash_id}
                    videoHashId={videoId!}
                    currentVideoShot={currentVideoShot}
                    aesKey={currentVideo?.details.keyframe_aes_key_id}
                  />
                </div> */}
                <div className={`${selectedTab === 6 ? 'visible' : 'invisible'}`}>
                  {videoId && currentVideo && (
                    <ExternalTimelineView
                      videoHashId={videoId}
                      videoShotId={currentVideo.details.video_shots?.[currentVideoShot]?.shot_hash_id}
                      aesKeyId={currentVideo.details?.keyframe_aes_key_id}
                      encrytedKeys={currentVideo?.details?.enc_data}
                      currentVideoShot={currentVideoShot}
                    ></ExternalTimelineView>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        {/* <ChatIntercomIcon handleClick={toggleChat} isChatOpen={isChatOpen} />
        {isChatOpen && <ChatWindow videoId={videoId} viewer={currentVideo?.viewer} />} */}
      </div>
      <div className="block md:hidden"></div>
    </>
  );
};

export default ExternalVideoPage;
